import { useCallback, useState } from "react";
import { getApprovalEmployeesList, getLeavesViaEmployee, getTourPlansViaEmployee } from "../api/approval";
import { DownlineEmployeeLeaves, DownlineEmployees, DownlineEmployeeTourPlans } from "../types/approval";


/** UI model used by Approvals screen */
export interface ApprovalUser {
    id: string;
    name: string;
    designation: "MR" | "ASM" | "ZSM" | "RSM";
    avatar: string;
    region?: string;
    tenant_id: number;
    employee_id: number;
    totalTourPlans: number;
    pendingTourPlans: number;
    approvedTourPlans: number;
    rejectedTourPlans: number;
    pendingDeviation: number;
    approvedDeviation: number;
    rejectedDeviation: number;

    totalLeaves: number;
    pendingLeaves: number;
    approvedLeaves: number;
    rejectedLeaves: number;
}

/** UI Tour Plan model */
export interface ApprovalTourPlan {
    id: number;
    date: string;

    status: string;
    locationName: string;
    headQuarterId: number;
    approved_by: string | null;
    deviation?: {
        id: number;
        tenant_id: number;
        employee_id: number;
        tour_plan_id: number;
        old_location_id: number;
        new_location_id: number;
        remarks: string;
        status: string;
        approved_by: number | null;
        created_at: string;
        updated_at?: string;
        deleted_at?: string;
        old_location: {
            id: number;
            tenant_id: number;
            head_quarter_id: number;
            name: string;
            latitude: string;
            longitude: string;
            created_at: string;
            updated_at?: string;
            deleted_at?: string;
        },
        new_location: {
            id: number;
            tenant_id: number;
            head_quarter_id: number;
            name: string;
            latitude: string;
            longitude: string;
            created_at: string;
            updated_at?: string;
            deleted_at?: string;
        }
    }
}
/** UI Tour Plan model */
export interface ApprovalLeaves {
    id: number;
    date: string;
    status: string;
    leaveType: string;
    reason?: string
}

export const useApprovalFlow = () => {
    const [downlineEmployees, setDownlineEmployees] = useState<DownlineEmployees[]>([]);
    const [users, setUsers] = useState<ApprovalUser[]>([]);
    const [tourPlans, setTourPlans] = useState<ApprovalTourPlan[]>([]);
    const [leaves, setLeaves] = useState<ApprovalLeaves[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    /** 🔹 Fetch approval list */
    const fetchApprovalEmployeesList = useCallback(async (tenantId: number) => {
        try {
            setLoading(true);

            const res = await getApprovalEmployeesList(tenantId);
            const employees: DownlineEmployees[] = res.data.data;

            setDownlineEmployees(employees);

            const mappedUsers: ApprovalUser[] = employees.map(emp => ({
                id: String(emp.user.id),
                name: emp.user.name,
                designation: emp.designation.name as ApprovalUser["designation"],
                avatar: getInitials(emp.user.name),
                region: emp.headquarter?.name,
                tenant_id: tenantId,
                employee_id: emp.id,

                totalTourPlans:
                    emp.tour_plan_count.submitted +
                    emp.tour_plan_count.approved +
                    emp.tour_plan_count.rejected,

                pendingTourPlans: emp.tour_plan_count.submitted,
                approvedTourPlans: emp.tour_plan_count.approved,
                rejectedTourPlans: emp.tour_plan_count.rejected,
                pendingDeviation: emp.deviation_count.submitted,
                approvedDeviation: emp.deviation_count.approved,
                rejectedDeviation: emp.deviation_count.rejected,

                totalLeaves:
                    emp.leave_count.submitted +
                    emp.leave_count.approved +
                    emp.leave_count.rejected,

                pendingLeaves: emp.leave_count.submitted,
                approvedLeaves: emp.leave_count.approved,
                rejectedLeaves: emp.leave_count.rejected,
            }));

            setUsers(mappedUsers);
        } finally {
            setLoading(false);
        }
    }, []);

    /** 🔹 Fetch tour plans for a selected employee */
    const fetchEmployeeTourPlans = useCallback(async (tenantId: number, employeeId: number) => {
        try {
            console.log('fetchEmployeeTourPlans called with:', { tenantId, employeeId });
            setLoading(true);
            setSelectedEmployee(employeeId);

            const res = await getTourPlansViaEmployee(tenantId, employeeId);

            const details: DownlineEmployeeTourPlans = res.data.data;

            const mappedTourPlans: ApprovalTourPlan[] = details.tour_plans.map(tp => ({
                id: tp.id,
                date: tp.tour_plan_date!,
                status: tp.status === "submitted" ? "pending" : tp.status,
                locationName: tp.location!.name,
                headQuarterId: tp.location!.head_quarter_id,
                deviation: tp.deviation,
                approved_by: tp.approved_by_user?.name ?? ""
            }));

            setTourPlans(mappedTourPlans);
        } catch (error) {
            console.error('Error fetching tour plans:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    /** 🔹 Fetch leaves for a selected employee */
    const fetchEmployeeLeaves = useCallback(async (tenantId: number, employeeId: number) => {
        try {
            console.log('fetchEmployeeLeaves called with:', { tenantId, employeeId });
            setLoading(true);
            setSelectedEmployee(employeeId);

            const res = await getLeavesViaEmployee(tenantId, employeeId);

            const details: DownlineEmployeeLeaves = res.data.data;

            const mappedLeaves: ApprovalLeaves[] = details.leaves.map(tp => ({
                id: tp.id,
                date: tp.leave_date!,
                status: tp.status === "submitted" ? "pending" : tp.status,
                leaveType: tp.leave_type!,
                reason: tp.reason,
            }));

            setLeaves(mappedLeaves);
        } catch (error) {
            console.error('Error fetching tour plans:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        downlineEmployees,
        users,
        tourPlans,
        selectedEmployee,
        loading,
        fetchApprovalEmployeesList,
        fetchEmployeeTourPlans,
        fetchEmployeeLeaves,
        leaves,
    };
};

/** shared util */
const getInitials = (name: string) =>
    name
        .split(" ")
        .map(w => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
